import gsap from 'gsap'
import { ScrollToPlugin } from 'gsap/all'

gsap.registerPlugin(ScrollToPlugin)




/* UTILITIES
---------------------------------------------------------------------------------------------------- */

export const sample = arr => arr[Math.floor(Math.random() * arr.length)]


export const random = (min, max) => Math.floor(Math.random() * (max - min)) + min


export const sum = values => values.reduce((currSum, value) => currSum + value, 0)


export const mean = values => sum(values) / values.length


export const clamp = (val, min = 0, max = 1) => Math.max(min, Math.min(max, val))


export const roundTo = (number, places = 0) => Math.round(number * 10 ** places) / 10 ** places


export const lerp = (start, finish, progress) => ((finish - start) * progress) + start


export const radians = angle => angle * (Math.PI / 180)


export const degrees = angle => angle * (180 / Math.PI)


export const map = (value, min1, max1, min2, max2) => min2 + (max2 - min2) * (value - min1) / (max1 - min1)


export const sigmoid = x => x / (1 + Math.abs(x))


export const uID = () => `_${Math.random().toString(36).substr(2, 9)}`


export const debounce = (callback, wait, timeoutId = null) => (...args) => {
    window.clearTimeout(timeoutId)

    timeoutId = setTimeout(() => {
        callback(...args)
    }, wait)
}


export const randomN = (arr, n) => (
    arr.map(x => ({ x, r: Math.random() }))
        .sort((a, b) => a.r - b.r)
        .map(a => a.x)
        .slice(0, n)
)


export const isEmpty = obj => Object.keys(obj).length === 0


export const omit = (obj, key) => {
    const newObj = {}

    for (const name in obj) {
        if (name !== key) {
            newObj[name] = obj[name]
        }
    }

    return newObj
}


// Either removes or adds an item to an array
// EXAMPLE: toggleInArray([1, 2], 3) -> [1, 2, 3]
// EXAMPLE: toggleInArray([1, 2], 2) -> [1]
export const toggleInArray = (arr, item) => (
    arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]
)

// Combines 2 arrays, removing duplicates.
// EXAMPLE: mergeUnique([1, 2], [2, 3]) -> [1, 2, 3]
export const mergeUnique = (arr1, arr2) => (
    arr1.concat(arr2.filter(item => arr1.indexOf(item) === -1))
)


export const deleteCookie = (key) => {
    document.cookie = `${encodeURIComponent(key)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`
}


export const convertHexToRGBA = (hex, alpha = 1) => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)

    return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const rgb2hex = (color) => {
    const rgb = color.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i)
    return (rgb && rgb.length === 4) ? `#${
        (`0${parseInt(rgb[1], 10).toString(16)}`).slice(-2)
    }${(`0${parseInt(rgb[2], 10).toString(16)}`).slice(-2)
    }${(`0${parseInt(rgb[3], 10).toString(16)}`).slice(-2)}` : ''
}


export const wrap = (el, wrapper) => {
    el.parentNode.insertBefore(wrapper, el)
    wrapper.appendChild(el)
}

export const innerWrap = (el, wrapper) => {
    el.parentNode.insertBefore(wrapper, el)
    wrapper.innerHTML = el.innerHTML
    el.innerHTML = ''

    el.appendChild(wrapper)
}


export const unwrap = (content, isHTML = false) => {
    for (let i = 0; i < content.length; i++) {
        const el     = content[i]
        const parent = el.parentNode

        if (parent.parentNode) parent.outerHTML = !isHTML ? el.textContent : el.outerHTML
    }
}


export const unwrapLine = (line) => {
    for (let i = 0; i < line.length; i++) {
        const el     = line[i]
        const parent = el.parentNode

        while (el.firstChild) parent.insertBefore(el.firstChild, el)

        parent.removeChild(el)
    }
}


export const getURLParams = (url) => {
    const params = {}

    const parser = document.createElement('a')
    parser.href = url

    const query = parser.search.substring(1)
    const vars = query.split('&')

    for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split('=')

        if (pair[0]) {
            params[pair[0]] = decodeURIComponent(pair[1]).split(',')
        }
    }

    return params
}


export const autoScrollTo = (yVal, dur = 1, cb) => {
    if (APP.Browser.isiOS) {
        const scrollVal = { y: window.pageYOffset }

        gsap.to(scrollVal, dur, {
            y          : yVal,
            ease       : 'power3.inOut',
            onUpdate   : () => { window.scrollTo(0, scrollVal.y) },
            onComplete : () => { if (cb) cb.call() },
        })
    } else {
        gsap.to(window, dur, {
            scrollTo: { y: yVal },
            ease: 'power3.inOut',
            onComplete : () => { if (cb) cb.call() },
        })
    }
}


export const slugify = (s) => {
    let str = s.replace(/^\s+|\s+$/g, '')

    // Make the string lowercase
    str = str.toLowerCase()

    // Remove accents, swap ñ for n, etc
    const from = 'ÁÄÂÀÃÅČÇĆĎÉĚËÈÊẼĔȆÍÌÎÏŇÑÓÖÒÔÕØŘŔŠŤÚŮÜÙÛÝŸŽáäâàãåčçćďéěëèêẽĕȇíìîïňñóöòôõøðřŕšťúůüùûýÿžþÞĐđßÆa·/_,:;'
    const to   = 'AAAAAACCCDEEEEEEEEIIIINNOOOOOORRSTUUUUUYYZaaaaaacccdeeeeeeeeiiiinnooooooorrstuuuuuyyzbBDdBAa------'
    for (let i = 0, l = from.length; i < l; i++) {
        str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i))
    }

    // Remove invalid chars
    str = str.replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')

    return str
}


export const coverImageSize = (w, h, ratio) => {
    let width = w
    let height = h

    if (width * ratio < h) {
        width = height / ratio
    } else {
        height = width * ratio
    }

    return { w: width, h: height }
}


export const getPos = ({ changedTouches, clientX, clientY, target }) => {
    const x = changedTouches ? changedTouches[0].clientX : clientX
    const y = changedTouches ? changedTouches[0].clientY : clientY

    return {
        x, y, target,
    }
}


export const getXY = (obj) => {
    const trans = { x: 0, y: 0 }
    const style = window.getComputedStyle(obj)
    const transform = style.transform || style.webkitTransform || style.mozTransform

    if (!transform || transform === 'none') return trans

    let result
    let mat = transform.match(/^matrix3d\((.+)\)$/)

    if (mat) {
        result = parseFloat(mat[1].split(', ')[13])
    } else {
        mat = transform.match(/^matrix\((.+)\)$/)

        trans.x = mat ? parseFloat(mat[1].split(', ')[4]) : 0
        trans.y = mat ? parseFloat(mat[1].split(', ')[5]) : 0
    }

    result = trans

    return result
}

export const getPagePosition = (obj) => {
    let offX = obj.offsetLeft
    let offY = obj.offsetTop
    let parent = obj.offsetParent

    while (parent !== null) {
        offX += parent.offsetLeft
        offY += parent.offsetTop

        parent = parent.offsetParent
    }

    return {
        x: offX,
        y: offY,
    }
}


export const measure = ($el) => {
    const $clone = $el.cloneNode(true)
    $clone.style.position = 'absolute'
    $clone.style.visibility = 'hidden'
    $clone.style.display = 'inherit'

    document.body.appendChild($clone)
    const { width, height } = BB($clone)

    const marginLeft   = parseFloat(CS($clone, 'margin-left'), 10)
    const marginRight  = parseFloat(CS($clone, 'margin-right'), 10)
    const paddingLeft  = parseFloat(CS($clone, 'padding-left'), 10)
    const paddingRight = parseFloat(CS($clone, 'padding-right'), 10)
    const w = marginLeft + marginRight + paddingLeft + paddingRight

    $clone.remove()

    return { width: width + w, height }
}

// Breakpoints
const bp = { isMobile: 'm', isTablet: 'tp', isDesktop: 'd' }
export const getBP = () => bp[Object.keys(bp).filter(b => APP.Layout[b])[0]]


// BoundingBox
export const BB = $el => $el.getBoundingClientRect()


// ComputedStyle
export const CS = (el, property) => {
    const style = window.getComputedStyle(el)

    return (property) ? style.getPropertyValue(property) : style
}




/* Asynchronous Fetches
--------------------------------------------------------- */

export const asyncJSON = async url => ((await fetch(url)).json())

export const asyncText = async url => ((await fetch(url)).text())

export const asyncBlob = async url => ((await fetch(url)).blob())




